// The prototype's template runtime (prototype/support.js, "dc-runtime"), reduced to what rendering needs, so the compiled
// templates build exactly the React element tree the prototype builds in the browser. Ported verbatim where it matters:
// expression resolution (src/expr.ts), cssToObj, text interpolation, sc-if / sc-for, value/checked defaults, importantify.
import { createElement, Fragment, isValidElement, type ReactNode } from 'react';
import { tr } from '../i18n';

export type Vals = Record<string, unknown>;

// ---- src/expr.ts
const IDENT_RE = /^[A-Za-z_$][A-Za-z0-9_$]*/;
const NUMBER_RE = /^-?\d+(\.\d+)?$/;
export function resolve(vals: Vals, src: string): unknown {
  const expr = String(src).trim();
  if (!expr) return undefined;
  if (expr[0] === '(' && expr[expr.length - 1] === ')' && parensWrapWhole(expr)) return resolve(vals, expr.slice(1, -1));
  const eq = findTopLevelEquality(expr);
  if (eq) {
    const lv = resolve(vals, expr.slice(0, eq.index)), rv = resolve(vals, expr.slice(eq.index + eq.op.length));
    switch (eq.op) { case '===': return lv === rv; case '!==': return lv !== rv; case '==': return lv == rv; default: return lv != rv; }
  }
  if (expr[0] === '!') return !resolve(vals, expr.slice(1));
  if (expr === 'true') return true;
  if (expr === 'false') return false;
  if (expr === 'null') return null;
  if (expr === 'undefined') return undefined;
  if (NUMBER_RE.test(expr)) return Number(expr);
  if (expr.length >= 2 && (expr[0] === '"' || expr[0] === "'") && expr[expr.length - 1] === expr[0]) return expr.slice(1, -1);
  return resolvePath(vals, expr);
}
function parensWrapWhole(expr: string): boolean {
  let depth = 0;
  for (let i = 0; i < expr.length - 1; i++) { if (expr[i] === '(') depth++; else if (expr[i] === ')') { depth--; if (depth === 0) return false; } }
  return true;
}
function findTopLevelEquality(expr: string): { index: number; op: string } | null {
  let depth = 0;
  for (let i = 0; i < expr.length; i++) {
    const c = expr[i];
    if (c === '[' || c === '(') depth++;
    else if (c === ']' || c === ')') depth--;
    else if (depth === 0 && (c === '=' || c === '!') && expr[i + 1] === '=') {
      if (i > 0 && (expr[i - 1] === '=' || expr[i - 1] === '!')) continue;
      if (!expr.slice(0, i).trim()) continue;
      return { index: i, op: expr[i + 2] === '=' ? c + '==' : c + '=' };
    }
  }
  return null;
}
function resolvePath(vals: Vals, expr: string): unknown {
  const head = expr.match(IDENT_RE); if (!head) return undefined;
  let cur: unknown = vals == null ? undefined : vals[head[0]], i = head[0].length;
  while (i < expr.length) {
    if (expr[i] === '.') {
      const m = expr.slice(i + 1).match(IDENT_RE) || expr.slice(i + 1).match(/^\d+/); if (!m) return undefined;
      cur = cur == null ? undefined : (cur as Record<string, unknown>)[m[0]]; i += 1 + m[0].length;
    } else if (expr[i] === '[') {
      let depth = 1, j = i + 1;
      while (j < expr.length && depth > 0) { if (expr[j] === '[') depth++; else if (expr[j] === ']') { depth--; if (depth === 0) break; } j++; }
      if (depth !== 0) return undefined;
      const key = resolve(vals, expr.slice(i + 1, j)) as string;
      cur = cur == null ? undefined : (cur as Record<string, unknown>)[key]; i = j + 1;
    } else return undefined;
  }
  return cur;
}

// ---- src/encode.ts / src/compile.ts
const kebabToCamel = (s: string) => s.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase());
export function cssToObj(css: string): Record<string, string> {
  const o: Record<string, string> = {};
  for (const decl of css.split(';')) {
    const i = decl.indexOf(':'); if (i < 0) continue;
    const prop = decl.slice(0, i).trim();
    o[prop.startsWith('--') ? prop : kebabToCamel(prop)] = decl.slice(i + 1).trim();
  }
  return o;
}
/** Attribute value: a whole binding returns the value as is; mixed text joins its parts (?? ''); else the literal. */
export const attr = (vals: Vals, parts: readonly string[]): unknown =>
  parts.length === 1 ? resolve(vals, parts[0]) : parts.map((s, i) => (i & 1 ? (resolve(vals, s) ?? '') : s)).join('');

/** Texts a screen reader or the browser shows: translated like the visible text (i18n, 23 Oct 2026). */
const TEXT_PROPS = new Set(['placeholder', 'aria-label', 'title', 'alt']);
/** Extra nodes inside the element with this data-dc-tpl (the language toggle), at its start or end. Set per page. */
export const slots: Record<string, { at: 'start' | 'end'; node: () => ReactNode }> = {};

/** walkElement: style strings become objects; an unresolved value/checked becomes ''/false (controlled inputs). */
export function el(tag: string, props: Record<string, unknown>, ...kids: ReactNode[]): ReactNode {
  for (const k in props) {
    let v = props[k];
    if (k === 'style' && typeof v === 'string') v = cssToObj(v);
    if ((k === 'value' || k === 'checked') && v === undefined) v = k === 'checked' ? false : '';
    if (TEXT_PROPS.has(k) && typeof v === 'string') v = tr(v);
    props[k] = v;
  }
  const slot = slots[props['data-dc-tpl'] as string];
  const out = kids.map(c => (typeof c === 'string' ? tr(c) : c));
  if (!slot) return createElement(tag, props, ...out);
  const extra = createElement(Fragment, { key: 'slot' }, slot.node());
  return createElement(tag, props, ...(slot.at === 'start' ? [extra, ...out] : [...out, extra]));
}

/** walkText with bindings: each value in a <span class="sc-interp">; undefined, null and booleans render nothing. */
export function text(vals: Vals, key: number, parts: readonly string[]): ReactNode {
  return createElement(Fragment, { key }, ...parts.map((p, i) => {
    if (!(i & 1)) return tr(p);
    const v = resolve(vals, p);
    if (v === undefined || v === null || typeof v === 'boolean') return null;
    if (isValidElement(v) || Array.isArray(v)) return createElement(Fragment, { key: i }, v as ReactNode);
    return createElement('span', { key: i, className: 'sc-interp' }, typeof v === 'string' ? tr(v) : String(v));
  }));
}

export function when(vals: Vals, key: number, value: string, kids: (v: Vals) => ReactNode[]): ReactNode {
  return resolve(vals, value) ? createElement(Fragment, { key }, ...kids(vals)) : null;
}

export function each(vals: Vals, key: number, list: string, as: string, kids: (v: Vals) => ReactNode[]): ReactNode {
  const l = resolve(vals, list);
  return createElement(Fragment, { key }, (Array.isArray(l) ? l : []).map((item, i) =>
    createElement(Fragment, { key: i }, ...kids({ ...vals, [as]: item, $index: i }))));
}

// ---- src/helmet.ts: pseudo-class rules are emitted with !important on every declaration.
export function importantify(css: string): string {
  const decls: string[] = []; let start = 0, depth = 0, quote = '';
  for (let i = 0; i < css.length; i++) {
    const c = css[i];
    if (quote) { if (c === '\\') i++; else if (c === quote) quote = ''; }
    else if (c === "'" || c === '"') quote = c;
    else if (c === '(') depth++;
    else if (c === ')') depth = Math.max(0, depth - 1);
    else if (c === ';' && depth === 0) { decls.push(css.slice(start, i)); start = i + 1; }
  }
  decls.push(css.slice(start));
  return decls.map(d => d.trim()).filter(Boolean).map(d => (/!\s*important$/i.test(d) ? d : d + ' !important')).join(';');
}
